<template>
  <div class="dicom-viewer-page">
    <!-- Header -->
    <div class="viewer-header">
      <div class="header-left">
        <h4 class="viewer-title">
          DICOM Viewer
        </h4>
        <small v-if="metadata" class="metadata-summary">
          {{ metadata.patientName }} | {{ metadata.modality }} | {{ metadata.studyDate }}
        </small>
      </div>
      
      <div class="header-controls">
        <b-button-group size="sm">
          <b-button variant="outline-secondary" @click="zoomIn" v-b-tooltip.hover title="Zoom In">
            <i class="fas fa-search-plus"></i>
          </b-button>
          <b-button variant="outline-secondary" @click="zoomOut" v-b-tooltip.hover title="Zoom Out">
            <i class="fas fa-search-minus"></i>
          </b-button>
          <b-button variant="outline-secondary" @click="resetView" v-b-tooltip.hover title="Reset View">
            <i class="fas fa-redo"></i>
          </b-button>
          <b-button variant="outline-secondary" @click="downloadImage" v-b-tooltip.hover title="Download">
            <i class="fas fa-download"></i>
          </b-button>
        </b-button-group>
        
        <!-- Brightness/Contrast Controls -->
        <div class="header-image-controls ml-3">
          <div class="header-control-item">
            <label class="control-label">Brightness</label>
            <input 
              type="range" 
              v-model="brightness" 
              @input="applyImageFilters"
              min="-50" 
              max="50" 
              step="5"
              class="header-range-input"
            />
            <span class="control-value">{{ brightness }}%</span>
          </div>
          <div class="header-control-item">
            <label class="control-label">Contrast</label>
            <input 
              type="range" 
              v-model="contrast" 
              @input="applyImageFilters"
              min="-50" 
              max="50" 
              step="5"
              class="header-range-input"
            />
            <span class="control-value">{{ contrast }}%</span>
          </div>
        </div>
        
        <b-button variant="outline-secondary" size="sm" class="ml-3" @click="toggleMetadata" v-b-tooltip.hover title="Toggle Metadata">
          <i class="fas fa-info-circle mr-1"></i> Info
        </b-button>
        
        <b-button variant="outline-danger" size="sm" class="ml-2" @click="closePage">
          <i class="fas fa-times"></i>
        </b-button>
      </div>
    </div>

    <!-- Main Viewer Area -->
    <div class="viewer-content">
      <div class="dicom-viewport-container" :class="{ 'with-sidebar': showMetadata }">
        <div 
          class="dicom-viewport" 
          ref="dicomViewport"
          @wheel="handleWheel"
          @mousedown="handleMouseDown"
          @mousemove="handleMouseMove"
          @mouseup="handleMouseUp"
          @mouseleave="handleMouseLeave"
        >
          <canvas ref="dicomCanvas" class="dicom-canvas"></canvas>
          
          <!-- Loading State -->
          <div v-if="isLoading" class="viewer-overlay loading-state">
            <div class="loading-content">
              <b-spinner variant="primary" label="Loading..."></b-spinner>
              <h5 class="mt-3">Loading DICOM Image...</h5>
              <p class="text-muted">Please wait while we process the medical image</p>
            </div>
          </div>
          
          <!-- Error State -->
          <div v-if="error" class="viewer-overlay error-state">
            <div class="error-content">
              <i class="fas fa-exclamation-triangle fa-4x text-warning mb-3"></i>
              <h5>Failed to Load DICOM Image</h5>
              <p class="error-message">{{ error }}</p>
              <div class="error-actions">
                <b-button variant="outline-secondary" @click="retryLoad" class="mr-2">
                  <i class="fas fa-redo mr-2"></i>Retry
                </b-button>
                <b-button variant="outline-info" @click="tryAsRegularImage">
                  <i class="fas fa-image mr-2"></i>Try as Regular Image
                </b-button>
              </div>
              <div class="error-details mt-3">
                <small class="text-muted">
                  <strong>Decoded URL:</strong><br>
                  <code>{{ imageUrl }}</code>
                </small>
              </div>
            </div>
          </div>
        </div>
        
        <!-- Metadata Sidebar -->
        <div v-if="showMetadata && metadata" class="metadata-sidebar">
          <div class="sidebar-header">
            <h6><i class="fas fa-info-circle mr-2"></i>Image Information</h6>
            <b-button variant="link" size="sm" @click="showMetadata = false">
              <i class="fas fa-times"></i>
            </b-button>
          </div>
          
          <div class="sidebar-content">
            <div class="info-section">
              <h6 class="section-title">Patient Information</h6>
              <div class="info-item">
                <span class="info-label">Name:</span>
                <span class="info-value">{{ metadata.patientName }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">DN:</span>
                <span class="info-value">{{ metadata.patientId }}</span>
              </div>
            </div>
            
            <div class="info-section">
              <h6 class="section-title">Study Details</h6>
              <div class="info-item">
                <span class="info-label">Date:</span>
                <span class="info-value">{{ metadata.studyDate }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Modality:</span>
                <span class="info-value">{{ metadata.modality }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Study:</span>
                <span class="info-value">{{ metadata.studyDescription }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Series:</span>
                <span class="info-value">{{ metadata.seriesDescription }}</span>
              </div>
            </div>
            
            <div class="info-section">
              <h6 class="section-title">Image Properties</h6>
              <div class="info-item">
                <span class="info-label">Dimensions:</span>
                <span class="info-value">{{ metadata.width }} × {{ metadata.height }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Window Width:</span>
                <span class="info-value">{{ currentWindowWidth }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Window Center:</span>
                <span class="info-value">{{ currentWindowCenter }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Status Bar -->
    <div class="viewer-footer">
      <div class="status-info">
        <span v-if="zoomLevel" class="status-item">
          <i class="fas fa-search-plus mr-1"></i>{{ Math.round(zoomLevel * 100) }}%
        </span>
        <span v-if="currentWindowWidth && currentWindowCenter" class="status-item">
          <i class="fas fa-adjust mr-1"></i>W: {{ currentWindowWidth }} / C: {{ currentWindowCenter }}
        </span>
        <span v-if="metadata" class="status-item">
          <i class="fas fa-expand-arrows-alt mr-1"></i>{{ metadata.width }} × {{ metadata.height }}
        </span>
      </div>
      
      <div class="tool-info">
        <span v-if="activeTool" class="active-tool">
          <i class="fas fa-mouse-pointer mr-1"></i>{{ activeToolName }}
        </span>
      </div>
    </div>
  </div>
</template>

<script>
import { loadDicomWithDaikon, getDicomMetadataWithDaikon, renderDicomToCanvas, cleanupDaikon } from '@/utils/daikonUtils';

export default {
  name: 'DicomViewer',
  data() {
    return {
      isLoading: true,
      error: null,
      metadata: null,
      activeTool: 'pan',
      dicomImage: null,
      image: null,
      zoomLevel: 1,
      currentWindowWidth: null,
      currentWindowCenter: null,
      showMetadata: false,
      imageUrl: null,
      brightness: 0,
      contrast: 0,
      currentZoom: 1,
      originalImageData: null,
      // Pan functionality
      panX: 0,
      panY: 0,
      isDragging: false,
      lastMouseX: 0,
      lastMouseY: 0,
      // Performance optimization
      filterTimeout: null,
    };
  },
  computed: {
    activeToolName() {
      const toolNames = {
        'pan': 'Pan & Zoom',
        'windowLevel': 'Window/Level',
        'zoom': 'Zoom'
      };
      return toolNames[this.activeTool] || 'Pan & Zoom';
    }
  },
  async mounted() {
    // Get image URL from query parameters - check both new base64 format and legacy URL format
    const base64Data = this.$route.query.data;
    const legacyUrl = this.$route.query.url;
    
    if (!base64Data && !legacyUrl) {
      this.error = 'No DICOM image URL provided';
      this.isLoading = false;
      return;
    }
    
    // Handle URL extraction
    try {
      if (base64Data) {
        // New base64 format - decode safely
        console.log('Using base64 encoded URL parameter');
        try {
          this.imageUrl = atob(base64Data);
          console.log('Successfully decoded base64 URL:', this.imageUrl);
        } catch (base64Error) {
          console.error('Base64 decoding failed:', base64Error);
          this.error = 'Invalid base64 encoded URL data';
          this.isLoading = false;
          return;
        }
      } else {
        // Legacy URL format - handle carefully
        console.log('Using legacy URL parameter format');
        const originalUrlParam = legacyUrl;
        
        if (originalUrlParam.includes('%')) {
          try {
            this.imageUrl = decodeURIComponent(originalUrlParam);
            console.log('Decoded legacy URL parameter:', this.imageUrl);
          } catch (decodeError) {
            console.error('URL decoding failed:', decodeError);
            this.error = `URL decoding failed: ${decodeError.message}`;
            this.isLoading = false;
            return;
          }
        } else {
          this.imageUrl = originalUrlParam;
          console.log('Using legacy URL parameter as-is:', this.imageUrl);
        }
      }
      
      // Validate that we have a proper URL
      if (!this.imageUrl || !this.imageUrl.startsWith('http')) {
        this.error = 'Invalid URL format';
        this.isLoading = false;
        return;
      }
      
      // Validate AWS S3 pre-signed URL structure
      if (this.imageUrl.includes('dent-cloud-media')) {
        const hasAccessKey = this.imageUrl.includes('AWSAccessKeyId');
        const hasExpires = this.imageUrl.includes('Expires');
        const hasSignature = this.imageUrl.includes('Signature');
        const hasSecurityToken = this.imageUrl.includes('x-amz-security-token');
        
        console.log('AWS S3 URL validation:', {
          hasAccessKey,
          hasExpires,
          hasSignature,
          hasSecurityToken
        });
        
        if (!hasAccessKey || !hasExpires || !hasSignature || !hasSecurityToken) {
          console.warn('AWS S3 URL missing required parameters');
          this.error = 'Invalid AWS pre-signed URL: missing required authentication parameters';
          this.isLoading = false;
          return;
        }
        
        // Check security token for corruption indicators
        const tokenMatch = this.imageUrl.match(/x-amz-security-token=([^&]+)/);
        if (tokenMatch && tokenMatch[1].includes('//////////')) {
          console.warn('AWS security token appears corrupted');
          this.error = 'AWS security token corrupted. Please try refreshing to get a new URL.';
          this.isLoading = false;
          return;
        }
      }
      
      console.log('Final validated URL for DICOM loading:', this.imageUrl);
      
    } catch (err) {
      console.error('URL processing failed:', err);
      this.error = `URL processing failed: ${err.message}`;
      this.isLoading = false;
      return;
    }
    
    await this.loadDicomImage();
  },
  beforeDestroy() {
    this.cleanup();
  },
  methods: {
    async loadDicomImage() {
      this.isLoading = true;
      this.error = null;
      
      try {
        console.log('Starting Daikon DICOM loading process...');
        
        // Wait for the next tick to ensure DOM is ready
        await this.$nextTick();
        
        // Load DICOM with Daikon
        this.dicomImage = await loadDicomWithDaikon(this.imageUrl);
        
        console.log('Daikon loading successful, rendering to canvas...');
        
        // Render to canvas (now async for batch processing)
        const canvas = this.$refs.dicomCanvas;
        const success = await renderDicomToCanvas(this.dicomImage, canvas);
        
        if (!success) {
          throw new Error('Failed to render DICOM image to canvas');
        }
        
        // Store original image data for filters
        const ctx = canvas.getContext('2d');
        this.originalImageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        
        // Get metadata from Daikon
        this.metadata = getDicomMetadataWithDaikon(this.dicomImage);
        
        // Set initial display values
        this.currentWindowWidth = this.metadata.windowWidth;
        this.currentWindowCenter = this.metadata.windowCenter;
        this.zoomLevel = 1;
        
        console.log('DICOM viewer setup complete with Daikon');
        
      } catch (err) {
        console.error('Error loading DICOM with Daikon:', err);
        this.error = err.message || 'Failed to load DICOM image';
      } finally {
        this.isLoading = false;
      }
    },
    
    toggleMetadata() {
      this.showMetadata = !this.showMetadata;
    },
    
    retryLoad() {
      this.loadDicomImage();
    },
    
    zoomIn() {
      this.currentZoom = Math.min(this.currentZoom * 1.2, 5);
      this.applyTransform();
    },
    
    zoomOut() {
      this.currentZoom = Math.max(this.currentZoom * 0.8, 0.1);
      this.applyTransform();
    },
    
    resetView() {
      this.currentZoom = 1;
      this.brightness = 0;
      this.contrast = 0;
      this.panX = 0;
      this.panY = 0;
      this.applyTransform();
      this.applyImageFilters();
    },
    
    handleWheel(event) {
      event.preventDefault();
      if (event.deltaY < 0) {
        this.zoomIn();
      } else {
        this.zoomOut();
      }
    },
    
    applyTransform() {
      const canvas = this.$refs.dicomCanvas;
      if (canvas) {
        canvas.style.transform = `translate(${this.panX}px, ${this.panY}px) scale(${this.currentZoom})`;
        canvas.style.cursor = this.isDragging ? 'grabbing' : 'grab';
        this.zoomLevel = this.currentZoom;
      }
    },
    
    handleMouseDown(event) {
      if (event.button === 0) { // Left mouse button
        this.isDragging = true;
        this.lastMouseX = event.clientX;
        this.lastMouseY = event.clientY;
        event.preventDefault();
      }
    },
    
    handleMouseMove(event) {
      if (this.isDragging) {
        const deltaX = event.clientX - this.lastMouseX;
        const deltaY = event.clientY - this.lastMouseY;
        
        this.panX += deltaX;
        this.panY += deltaY;
        
        this.lastMouseX = event.clientX;
        this.lastMouseY = event.clientY;
        
        this.applyTransform();
        event.preventDefault();
      }
    },
    
    handleMouseUp(event) {
      if (event.button === 0) {
        this.isDragging = false;
        this.applyTransform();
      }
    },
    
    handleMouseLeave() {
      this.isDragging = false;
      this.applyTransform();
    },
    
    applyImageFilters() {
      const canvas = this.$refs.dicomCanvas;
      if (!canvas) return;
      
      // Clear existing timeout
      if (this.filterTimeout) {
        clearTimeout(this.filterTimeout);
      }
      
      // Debounce the filter application to improve performance
      this.filterTimeout = setTimeout(() => {
        // Use CSS filters for better performance
        const brightnessValue = 1 + (this.brightness / 100); // Convert to CSS brightness scale
        const contrastValue = 1 + (this.contrast / 100); // Convert to CSS contrast scale
        
        // Apply CSS filters instead of pixel manipulation
        canvas.style.filter = `brightness(${brightnessValue}) contrast(${contrastValue})`;
      }, 50); // 50ms debounce
    },
    
    downloadImage() {
      const canvas = this.$refs.dicomCanvas;
      if (!canvas) return;
      
      const dataUrl = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `dicom_${Date.now()}.png`;
      link.click();
    },
    
    async tryAsRegularImage() {
      // Fallback: try to display as a regular image using basic canvas
      this.isLoading = true;
      const originalError = this.error; // Store the original DICOM error
      this.error = null;
      
      try {
        console.log('Attempting to load as regular image:', this.imageUrl);
        
        // Create a simple image element to test loading
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        const imageLoadPromise = new Promise((resolve, reject) => {
          img.onload = () => {
            console.log('Image loaded successfully as regular image');
            resolve();
          };
          img.onerror = (err) => {
            console.log('Failed to load as regular image:', err);
            reject(new Error('Failed to load image'));
          };
          // Set the source after setting up event handlers
          img.src = this.imageUrl;
        });
        
        // Add a timeout to prevent hanging
        const timeoutPromise = new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Image load timeout')), 10000)
        );
        
        await Promise.race([imageLoadPromise, timeoutPromise]);
        
        // If we get here, the image loaded successfully
        // Draw it on our canvas
        const canvas = this.$refs.dicomCanvas;
        const viewport = this.$refs.dicomViewport;
        
        if (canvas && viewport) {
          // Calculate dimensions to fit viewport while maintaining aspect ratio
          const viewportWidth = viewport.clientWidth;
          const viewportHeight = viewport.clientHeight;
          
          const scale = Math.min(
            viewportWidth / img.width,
            viewportHeight / img.height,
            1 // Don't scale up
          );
          
          const displayWidth = img.width * scale;
          const displayHeight = img.height * scale;
          
          canvas.width = displayWidth;
          canvas.height = displayHeight;
          
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, displayWidth, displayHeight);
          
          // Apply some basic image enhancements for X-ray images
          ctx.filter = 'contrast(1.2) brightness(1.1)';
          ctx.drawImage(img, 0, 0, displayWidth, displayHeight);
          ctx.filter = 'none'; // Reset filter
          
          // Set up basic metadata
          this.metadata = {
            patientName: 'N/A',
            patientId: 'N/A',
            studyDate: 'N/A',
            modality: 'IMAGE',
            studyDescription: 'Regular Image (Non-DICOM)',
            seriesDescription: 'N/A',
            width: img.width,
            height: img.height,
            windowWidth: 255,
            windowCenter: 127
          };
          
          this.currentWindowWidth = 255;
          this.currentWindowCenter = 127;
          this.zoomLevel = scale;
          
          // Enable basic tools for regular images
          this.activeTool = 'pan';
          
          console.log('Successfully displayed as regular image');
        }
        
      } catch (imgError) {
        console.error('Failed to load as regular image:', imgError);
        this.error = `Failed to load both as DICOM and regular image. The file may be inaccessible or the URL may be expired.

Original DICOM error: ${originalError}

Image load error: ${imgError.message}

Possible causes:
• The file URL has expired (S3 pre-signed URLs are time-limited)
• The file is not accessible due to permissions
• The file may be corrupted or not a valid image/DICOM file
• Network connectivity issues`;
      } finally {
        this.isLoading = false;
      }
    },
    
    closePage() {
      window.close();
    },
    
    cleanup() {
      console.log('Cleaning up Daikon resources...');
      cleanupDaikon();
      
      this.dicomImage = null;
    }
  }
};
</script>

<style scoped>
.dicom-viewer-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #1a1a1a;
  color: #fff;
  overflow: hidden;
}

/* Header */
.viewer-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  background: #2a2a2a;
  border-bottom: 1px solid #3a3a3a;
  flex-shrink: 0;
}

.header-left {
  display: flex;
  flex-direction: column;
}

.viewer-title {
  margin: 0;
  color: #fff;
  font-size: 1.2rem;
}

.metadata-summary {
  color: #999;
  margin-top: 0.25rem;
}

.header-controls {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

/* Main Content */
.viewer-content {
  flex: 1;
  display: flex;
  overflow: hidden;
}

.dicom-viewport-container {
  flex: 1;
  display: flex;
  transition: all 0.3s ease;
}

.dicom-viewport-container.with-sidebar {
  margin-right: 320px;
}

.dicom-viewport {
  flex: 1;
  position: relative;
  background: #000;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dicom-canvas {
  max-width: 100%;
  max-height: 100%;
  cursor: grab;
  transform-origin: center center;
  transition: filter 0.1s ease;
  user-select: none;
}

.dicom-canvas:active {
  cursor: grabbing;
}

/* Header Image Controls */
.header-image-controls {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem 1rem;
  border-left: 1px solid #3a3a3a;
  border-right: 1px solid #3a3a3a;
}

.header-control-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: #fff;
}

.control-label {
  margin: 0;
  font-weight: 500;
  min-width: 60px;
  color: #ccc;
}

.header-range-input {
  width: 80px;
  height: 3px;
  background: #555;
  outline: none;
  border-radius: 2px;
  cursor: pointer;
  transition: background 0.2s;
}

.header-range-input:hover {
  background: #666;
}

.header-range-input::-webkit-slider-thumb {
  appearance: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  border: 1px solid #0056b3;
}

.header-range-input::-moz-range-thumb {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #007bff;
  cursor: pointer;
  border: 1px solid #0056b3;
}

.control-value {
  min-width: 35px;
  text-align: right;
  font-weight: 500;
  color: #fff;
  font-size: 0.7rem;
}

/* Tool Cursors */
.tool-pan .dicom-canvas {
  cursor: move;
}

.tool-zoom .dicom-canvas {
  cursor: zoom-in;
}

.tool-window-level .dicom-canvas {
  cursor: ns-resize;
}

/* Overlay States */
.viewer-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.loading-state {
  background: rgba(0, 0, 0, 0.8);
}

.loading-content,
.error-content {
  text-align: center;
  padding: 2rem;
}

.error-state {
  background: rgba(0, 0, 0, 0.9);
}

.error-message {
  color: #ffc107;
  margin: 1rem 0;
}

.error-actions {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.error-details {
  max-width: 500px;
  word-break: break-all;
}

.error-details code {
  background: rgba(255, 255, 255, 0.1);
  padding: 0.25rem;
  border-radius: 3px;
  font-size: 0.8rem;
}

/* Metadata Sidebar */
.metadata-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  width: 320px;
  height: 100vh;
  background: #2a2a2a;
  border-left: 1px solid #3a3a3a;
  display: flex;
  flex-direction: column;
  z-index: 20;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #3a3a3a;
}

.sidebar-header h6 {
  margin: 0;
  color: #fff;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
}

.info-section {
  margin-bottom: 1.5rem;
}

.section-title {
  color: #007bff;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.75rem;
  padding-bottom: 0.25rem;
  border-bottom: 1px solid #3a3a3a;
}

.info-item {
  display: flex;
  justify-content: space-between;
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.info-label {
  color: #999;
  font-weight: 500;
}

.info-value {
  color: #fff;
  text-align: right;
  flex: 1;
  margin-left: 1rem;
  word-break: break-word;
}

/* Footer */
.viewer-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 1rem;
  background: #2a2a2a;
  border-top: 1px solid #3a3a3a;
  flex-shrink: 0;
  font-size: 0.875rem;
}

.status-info,
.tool-info {
  display: flex;
  gap: 1rem;
}

.status-item,
.active-tool {
  color: #999;
  display: flex;
  align-items: center;
}

.active-tool {
  color: #007bff;
  font-weight: 500;
}

/* Button Overrides */
.btn-outline-secondary {
  border-color: #6c757d;
  color: #6c757d;
}

.btn-outline-secondary:hover,
.btn-outline-secondary.active {
  background-color: #6c757d;
  border-color: #6c757d;
  color: #fff;
}

.btn-outline-danger:hover {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #fff;
}

/* Responsive */
@media (max-width: 768px) {
  .viewer-header {
    flex-direction: column;
    gap: 1rem;
    padding: 0.75rem;
  }
  
  .header-controls {
    justify-content: center;
    flex-wrap: wrap;
    gap: 0.5rem;
  }
  
  .header-image-controls {
    border: none;
    padding: 0.5rem;
    background: rgba(42, 42, 42, 0.8);
    border-radius: 6px;
    order: -1; /* Move controls to top on mobile */
    width: 100%;
    align-items: center;
  }
  
  .header-control-item {
    justify-content: center;
    text-align: center;
  }
  
  .control-label {
    min-width: auto;
    font-size: 0.7rem;
  }
  
  .header-range-input {
    width: 100px;
  }
  
  .metadata-sidebar {
    width: 100vw;
  }
  
  .dicom-viewport-container.with-sidebar {
    margin-right: 0;
  }
  
  .viewer-footer {
    flex-direction: column;
    gap: 0.5rem;
    text-align: center;
  }
}
</style>